/**
 * Every edit the builder can make, as one pure function.
 *
 * React-free so the awkward parts are testable without rendering: moving a
 * block between columns, changing a column count without dropping the blocks
 * that were in the column being removed, and an undo stack that does not treat
 * "the letter was loaded" as something to undo back past.
 *
 * The tree is two levels deep at most - top-level blocks, and leaves inside a
 * `columns` block - so a block is addressed by id alone and every operation
 * walks both levels. That is cheap at the sizes a letter reaches, and it keeps
 * the selection a single string rather than a path.
 */
import {
  cloneBlock,
  makeBlock,
  newBlockId,
  type Block,
  type BlockType,
  type ColumnSlot,
  type EmailDesign,
  type PageStyle,
} from "./types";

export interface BuilderState {
  design: EmailDesign;
  selectedId: string | null;
  /** Bounded: a letter is not a document editor, and an unbounded stack of
   *  whole designs is a memory leak that only shows up after an hour of work. */
  past: EmailDesign[];
  future: EmailDesign[];
}

const HISTORY_LIMIT = 50;

export type BuilderAction =
  /** Replace everything and forget the history. Loading, and applying a
   *  template - neither is an edit somebody should be able to undo INTO. */
  | { kind: "load"; design: EmailDesign }
  | { kind: "select"; id: string | null }
  | { kind: "page"; patch: Partial<PageStyle> }
  /** A negative index appends. See `at()`. */
  | { kind: "add"; type: BlockType; index: number }
  | { kind: "addToColumn"; parentId: string; colId: string; type: BlockType; index: number }
  /** An existing block dropped somewhere else. Same action for both directions:
   *  into a column when `target` names one, back to the top level when it does not. */
  | { kind: "move"; id: string; target: { colId: string } | null; index: number }
  | { kind: "update"; id: string; patch: Record<string, unknown> }
  | { kind: "remove"; id: string }
  | { kind: "duplicate"; id: string }
  | { kind: "columnCount"; id: string; count: number }
  | { kind: "undo" }
  | { kind: "redo" };

// ─────────────────────────────────────────────────────────────────────────
// Tree helpers
// ─────────────────────────────────────────────────────────────────────────

export function columnsOf(block: Block): ColumnSlot[] {
  const cols = (block.props as { cols?: ColumnSlot[] }).cols;
  return Array.isArray(cols) ? cols : [];
}

/** Every block in the design, parents before their children. */
export function walk(design: EmailDesign): Block[] {
  const out: Block[] = [];
  for (const b of design.blocks) {
    out.push(b);
    for (const c of columnsOf(b)) out.push(...(c.children ?? []));
  }
  return out;
}

export function findBlock(design: EmailDesign, id: string | null): Block | null {
  if (!id) return null;
  return walk(design).find((b) => b.id === id) ?? null;
}

/** Which column holds this block, if any. The inspector needs it to know that a
 *  block cannot be given a `columns` sibling inside a column. */
export function findParentColumn(design: EmailDesign, id: string): { block: Block; col: ColumnSlot } | null {
  for (const b of design.blocks) {
    for (const col of columnsOf(b)) {
      if ((col.children ?? []).some((c) => c.id === id)) return { block: b, col };
    }
  }
  return null;
}

/** The design with `id` gone from wherever it sits, and the block that left. */
function detach(design: EmailDesign, id: string): { design: EmailDesign; block: Block | null } {
  const top = design.blocks.find((b) => b.id === id);
  if (top) {
    return { design: { ...design, blocks: design.blocks.filter((b) => b.id !== id) }, block: top };
  }

  let removed: Block | null = null;
  const blocks = design.blocks.map((b) => {
    const cols = columnsOf(b);
    if (cols.length === 0) return b;
    let touched = false;
    const next = cols.map((col) => {
      const children = col.children ?? [];
      if (!children.some((c) => c.id === id)) return col;
      touched = true;
      removed = children.find((c) => c.id === id) ?? null;
      return { ...col, children: children.filter((c) => c.id !== id) };
    });
    return touched ? { ...b, props: { ...b.props, cols: next } } : b;
  });

  return { design: { ...design, blocks }, block: removed };
}

/** Where an insert actually lands. A NEGATIVE index means "at the end", which
 *  is not the same as passing the current length: the palette's Add button
 *  would then be reading a count from whatever render it last saw, and two
 *  quick clicks would both aim at the same stale position. */
function at(index: number, length: number): number {
  return index < 0 ? length : Math.min(index, length);
}

function insertTop(design: EmailDesign, block: Block, index: number): EmailDesign {
  const blocks = [...design.blocks];
  blocks.splice(at(index, blocks.length), 0, block);
  return { ...design, blocks };
}

function insertColumn(design: EmailDesign, colId: string, block: Block, index: number): EmailDesign {
  const blocks = design.blocks.map((b) => {
    const cols = columnsOf(b);
    if (!cols.some((c) => c.id === colId)) return b;
    const next = cols.map((col) => {
      if (col.id !== colId) return col;
      const children = [...(col.children ?? [])];
      children.splice(at(index, children.length), 0, block);
      return { ...col, children };
    });
    return { ...b, props: { ...b.props, cols: next } };
  });
  return { ...design, blocks };
}

function mapBlock(design: EmailDesign, id: string, fn: (b: Block) => Block): EmailDesign {
  const blocks = design.blocks.map((b) => {
    if (b.id === id) return fn(b);
    const cols = columnsOf(b);
    if (cols.length === 0) return b;
    let touched = false;
    const next = cols.map((col) => {
      const children = col.children ?? [];
      if (!children.some((c) => c.id === id)) return col;
      touched = true;
      return { ...col, children: children.map((c) => (c.id === id ? fn(c) : c)) };
    });
    return touched ? { ...b, props: { ...b.props, cols: next } } : b;
  });
  return { ...design, blocks };
}

/**
 * Grow or shrink a `columns` block.
 *
 * Shrinking keeps the blocks that were in the columns being dropped - they move
 * into the last surviving column. Losing them silently is the behaviour that
 * makes a builder untrustworthy: an operator nudges a number and the paragraph
 * they wrote is gone with no undo prompt and no warning.
 */
function resizeColumns(block: Block, count: number): Block {
  const cols = columnsOf(block);
  const want = Math.max(1, Math.min(4, count));
  if (cols.length === want) return block;

  if (want > cols.length) {
    const added = Array.from({ length: want - cols.length }, () => ({ id: newBlockId(), children: [] as Block[] }));
    return { ...block, props: { ...block.props, count: want, cols: [...cols, ...added] } };
  }

  const kept = cols.slice(0, want);
  const orphans = cols.slice(want).flatMap((c) => c.children ?? []);
  const last = kept[kept.length - 1]!;
  const next = [...kept.slice(0, -1), { ...last, children: [...(last.children ?? []), ...orphans] }];
  return { ...block, props: { ...block.props, count: want, cols: next } };
}

// ─────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────

function pushHistory(state: BuilderState, design: EmailDesign, selectedId = state.selectedId): BuilderState {
  return {
    design,
    selectedId,
    past: [...state.past, state.design].slice(-HISTORY_LIMIT),
    future: [],
  };
}

export function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.kind) {
    case "load":
      return { design: action.design, selectedId: null, past: [], future: [] };

    case "select":
      return { ...state, selectedId: action.id };

    case "page":
      return pushHistory(state, { ...state.design, page: { ...state.design.page, ...action.patch } });

    case "add": {
      const block = makeBlock(action.type);
      return pushHistory(state, insertTop(state.design, block, action.index), block.id);
    }

    case "addToColumn": {
      const block = makeBlock(action.type);
      return pushHistory(state, insertColumn(state.design, action.colId, block, action.index), block.id);
    }

    case "move": {
      const { design, block } = detach(state.design, action.id);
      if (!block) return state;
      const next = action.target
        ? insertColumn(design, action.target.colId, block, action.index)
        : insertTop(design, block, action.index);
      return pushHistory(state, next, action.id);
    }

    case "update":
      return pushHistory(
        state,
        mapBlock(state.design, action.id, (b) => ({ ...b, props: { ...b.props, ...action.patch } })),
      );

    case "remove": {
      const { design, block } = detach(state.design, action.id);
      if (!block) return state;
      return pushHistory(state, design, state.selectedId === action.id ? null : state.selectedId);
    }

    case "duplicate": {
      const source = findBlock(state.design, action.id);
      if (!source) return state;
      const copy = cloneBlock(source);
      const parent = findParentColumn(state.design, action.id);
      if (parent) {
        const at = (parent.col.children ?? []).findIndex((c) => c.id === action.id);
        return pushHistory(state, insertColumn(state.design, parent.col.id, copy, at + 1), copy.id);
      }
      const at = state.design.blocks.findIndex((b) => b.id === action.id);
      return pushHistory(state, insertTop(state.design, copy, at + 1), copy.id);
    }

    case "columnCount":
      return pushHistory(state, mapBlock(state.design, action.id, (b) => resizeColumns(b, action.count)));

    case "undo": {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        design: previous,
        // A selection that no longer exists in the restored design would leave
        // the inspector editing a block nobody can see.
        selectedId: findBlock(previous, state.selectedId) ? state.selectedId : null,
        past: state.past.slice(0, -1),
        future: [state.design, ...state.future].slice(0, HISTORY_LIMIT),
      };
    }

    case "redo": {
      const next = state.future[0];
      if (!next) return state;
      return {
        design: next,
        selectedId: findBlock(next, state.selectedId) ? state.selectedId : null,
        past: [...state.past, state.design].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
      };
    }

    default:
      return state;
  }
}
